import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ChefHat, UtensilsCrossed, Truck, Star, ArrowRight, Phone, MapPin,
  Clock, QrCode, Sparkles, Wine
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import SmartRecommendations from '../components/SmartRecommendations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// === Image library (curated by design agent) ===
const HERO_BG = 'https://images.unsplash.com/photo-1612453329651-4ca61a6760a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtZWRpdGVycmFuZWFuJTIwcmVzdGF1cmFudCUyMHRlcnJhY2V8ZW58MHx8fHwxNzc3Mzc2OTMzfDA&ixlib=rb-4.1.0&q=85';
const ALAKART_IMG = 'https://images.pexels.com/photos/32525175/pexels-photo-32525175.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const CATERING_IMG = 'https://images.pexels.com/photos/6297198/pexels-photo-6297198.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const DRINKS_IMG = 'https://images.pexels.com/photos/15866914/pexels-photo-15866914.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const AMBIANCE_IMG = 'https://images.unsplash.com/photo-1760726743604-fb4e71718901?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxtZWRpdGVycmFuZWFuJTIwcmVzdGF1cmFudCUyMHRlcnJhY2V8ZW58MHx8fHwxNzc3Mzc2OTMzfDA&ixlib=rb-4.1.0&q=85';

const I18N = {
  tr: {
    badge: 'Akdeniz Mutfağı',
    heroTitle: 'Central Hotel',
    heroAccent: '& Restaurant',
    heroSub: 'Karadağ\'ın kalbinde sıcak servis ve taze lezzetlerin buluşması.',
    orderNow: 'Şimdi Sipariş Ver',
    seeMenu: 'Menüyü Keşfet',
    socialProof: '5000+ memnun misafir · 4.9 puan ortalaması',
    showcaseTitle: 'Lezzet Kategorilerimiz',
    showcaseSub: 'Her damak için özenle seçilmiş zenginlik',
    alakart: 'A La Carte',
    alakartDesc: 'Şefimizin imza tabakları',
    catering: 'Günlük Menü',
    cateringDesc: 'Her gün taze, ev yapımı',
    beverages: 'İçecekler',
    beveragesDesc: 'Şarap, kahve & kokteyller',
    whyUs: 'Neden Central?',
    whyFeatures: [
      { icon: UtensilsCrossed, title: 'Taze Malzemeler', desc: 'Yerel üreticilerden günlük tedarik' },
      { icon: ChefHat, title: 'Şefimizin İmzası', desc: 'Geleneksel tarifler, modern sunum' },
      { icon: Truck, title: 'Hızlı Paket Servis', desc: 'Sipariş kapınızda 30 dk içinde' }
    ],
    qrTitle: 'Masada mısınız?',
    qrDesc: 'Masa QR kodunu okutun, anında sipariş verin.',
    hoursTitle: 'Çalışma Saatleri',
    open: 'Açığız',
    closed: 'Kapalıyız',
    contactTitle: 'Bize Ulaşın',
    address: 'Adres',
    phone: 'Telefon',
    callNow: 'Şimdi Ara',
    getDirections: 'Yol Tarifi Al',
    tagline: 'Afiyet Olsun!'
  },
  en: {
    badge: 'Mediterranean Cuisine',
    heroTitle: 'Central Hotel',
    heroAccent: '& Restaurant',
    heroSub: 'Warm service and fresh flavors in the heart of Montenegro.',
    orderNow: 'Order Now',
    seeMenu: 'Explore Menu',
    socialProof: '5000+ happy guests · 4.9 average rating',
    showcaseTitle: 'Our Flavor Categories',
    showcaseSub: 'Carefully crafted variety for every palate',
    alakart: 'A La Carte',
    alakartDesc: "Chef's signature plates",
    catering: 'Daily Menu',
    cateringDesc: 'Fresh & home-style every day',
    beverages: 'Beverages',
    beveragesDesc: 'Wine, coffee & cocktails',
    whyUs: 'Why Central?',
    whyFeatures: [
      { icon: UtensilsCrossed, title: 'Fresh Ingredients', desc: 'Daily from local producers' },
      { icon: ChefHat, title: "Chef's Signature", desc: 'Traditional recipes, modern presentation' },
      { icon: Truck, title: 'Fast Delivery', desc: 'At your door within 30 min' }
    ],
    qrTitle: 'At a table?',
    qrDesc: 'Scan the table QR for instant ordering.',
    hoursTitle: 'Working Hours',
    open: 'Open Now',
    closed: 'Closed',
    contactTitle: 'Get in Touch',
    address: 'Address',
    phone: 'Phone',
    callNow: 'Call Now',
    getDirections: 'Get Directions',
    tagline: 'Bon Appétit!'
  },
  me: {
    badge: 'Mediteranska Kuhinja',
    heroTitle: 'Central Hotel',
    heroAccent: '& Restaurant',
    heroSub: 'Topla usluga i svježi okusi u srcu Crne Gore.',
    orderNow: 'Naruči Sada',
    seeMenu: 'Istraži Meni',
    socialProof: '5000+ zadovoljnih gostiju · 4.9 ocjena',
    showcaseTitle: 'Naše Kategorije Okusa',
    showcaseSub: 'Pažljivo odabrana raznolikost',
    alakart: 'A La Carte',
    alakartDesc: "Šefov potpisni izbor",
    catering: 'Dnevni Meni',
    cateringDesc: 'Svjež i domaći svaki dan',
    beverages: 'Pića',
    beveragesDesc: 'Vino, kafa & kokteli',
    whyUs: 'Zašto Central?',
    whyFeatures: [
      { icon: UtensilsCrossed, title: 'Svježi Sastojci', desc: 'Svježe od lokalnih proizvođača' },
      { icon: ChefHat, title: 'Šefov Potpis', desc: 'Tradicionalni recepti, moderna prezentacija' },
      { icon: Truck, title: 'Brza Dostava', desc: 'Na vaša vrata za 30 min' }
    ],
    qrTitle: 'Za stolom ste?',
    qrDesc: 'Skenirajte QR kod stola za trenutno naručivanje.',
    hoursTitle: 'Radno Vrijeme',
    open: 'Otvoreno',
    closed: 'Zatvoreno',
    contactTitle: 'Kontaktirajte Nas',
    address: 'Adresa',
    phone: 'Telefon',
    callNow: 'Pozovi',
    getDirections: 'Uputstva',
    tagline: 'Prijatno!'
  },
  ru: {
    badge: 'Средиземноморская кухня',
    heroTitle: 'Central Hotel',
    heroAccent: '& Restaurant',
    heroSub: 'Тёплое обслуживание и свежие вкусы в сердце Черногории.',
    orderNow: 'Заказать сейчас',
    seeMenu: 'Смотреть меню',
    socialProof: '5000+ довольных гостей · Рейтинг 4.9',
    showcaseTitle: 'Категории Вкуса',
    showcaseSub: 'Тщательно подобранное разнообразие',
    alakart: 'А Ла Карт',
    alakartDesc: 'Фирменные блюда шефа',
    catering: 'Дневное Меню',
    cateringDesc: 'Свежее и домашнее каждый день',
    beverages: 'Напитки',
    beveragesDesc: 'Вино, кофе и коктейли',
    whyUs: 'Почему Central?',
    whyFeatures: [
      { icon: UtensilsCrossed, title: 'Свежие Ингредиенты', desc: 'Ежедневно от местных производителей' },
      { icon: ChefHat, title: 'Подпись Шефа', desc: 'Традиционные рецепты, современная подача' },
      { icon: Truck, title: 'Быстрая Доставка', desc: 'У вашей двери за 30 минут' }
    ],
    qrTitle: 'За столом?',
    qrDesc: 'Сканируйте QR код стола для мгновенного заказа.',
    hoursTitle: 'Часы Работы',
    open: 'Открыто',
    closed: 'Закрыто',
    contactTitle: 'Свяжитесь с Нами',
    address: 'Адрес',
    phone: 'Телефон',
    callNow: 'Позвонить',
    getDirections: 'Маршрут',
    tagline: 'Приятного аппетита!'
  },
  sr: {
    badge: 'Медитеранска Кухиња',
    heroTitle: 'Central Hotel',
    heroAccent: '& Restaurant',
    heroSub: 'Топла услуга и свежи укуси у срцу Црне Горе.',
    orderNow: 'Наручи Сада',
    seeMenu: 'Истражи Мени',
    socialProof: '5000+ задовољних гостију · 4.9 оцена',
    showcaseTitle: 'Наше Категорије Укуса',
    showcaseSub: 'Пажљиво одабрана разноврсност',
    alakart: 'А Ла Карт',
    alakartDesc: 'Шефов потписни избор',
    catering: 'Дневни Мени',
    cateringDesc: 'Свеж и домаћи сваки дан',
    beverages: 'Пића',
    beveragesDesc: 'Вино, кафа и коктели',
    whyUs: 'Зашто Central?',
    whyFeatures: [
      { icon: UtensilsCrossed, title: 'Свежи Састојци', desc: 'Свеже од локалних произвођача' },
      { icon: ChefHat, title: 'Шефов Потпис', desc: 'Традиционални рецепти, модерна презентација' },
      { icon: Truck, title: 'Брза Достава', desc: 'На ваша врата за 30 мин' }
    ],
    qrTitle: 'За столом сте?',
    qrDesc: 'Скенирајте QR код стола за тренутно наручивање.',
    hoursTitle: 'Радно Време',
    open: 'Отворено',
    closed: 'Затворено',
    contactTitle: 'Контактирајте Нас',
    address: 'Адреса',
    phone: 'Телефон',
    callNow: 'Позови',
    getDirections: 'Упутства',
    tagline: 'Пријатно!'
  }
};

const RESTAURANT = {
  address: '13 Jula 6, Budva, Montenegro',
  phone: '+382 69 144 043',
  mapsUrl: 'https://maps.google.com/?q=Central+Hotel+Budva+Montenegro',
  // Embedded map for Budva town centre
  mapsEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2891.1!2d18.8378!3d42.2911!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0!2zNDLCsDE3JzI4LjAiTiAxOMKwNTAnMTYuMSJF!5e0!3m2!1str!2sus!4v0'
};

const Home = () => {
  const { language } = useLanguage();
  const t = I18N[language] || I18N.tr;
  const [isOpen, setIsOpen] = useState(null);
  const [openMessage, setOpenMessage] = useState('');
  const heroRef = useRef(null);

  // Parallax hero
  const { scrollY } = useScroll();
  const heroBgY = useTransform(scrollY, [0, 600], [0, 150]);
  const heroContentY = useTransform(scrollY, [0, 400], [0, -40]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.3]);

  useEffect(() => {
    axios.get(`${API}/settings/is-open`).then(r => {
      setIsOpen(r.data.is_open);
      setOpenMessage(r.data.message || '');
    }).catch(() => setIsOpen(true));
  }, []);

  return (
    <div className="min-h-screen bg-bone font-outfit text-charcoal">
      {/* === HERO === */}
      <section ref={heroRef} className="relative h-[100vh] min-h-[640px] overflow-hidden">
        <motion.div className="absolute inset-0 will-change-transform" style={{ y: heroBgY }}>
          <img src={HERO_BG} alt="" className="w-full h-full object-cover scale-110" />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-charcoal/55 to-charcoal/85" />
        </motion.div>

        <motion.div
          style={{ y: heroContentY, opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center max-w-7xl mx-auto px-6 md:px-12"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex w-fit items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md ring-1 ring-white/20 text-white/90 text-xs uppercase tracking-[0.25em] mb-6"
          >
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            {t.badge}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-playfair text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] tracking-tight text-white leading-[0.95] max-w-4xl"
          >
            {t.heroTitle}
            <span className="block text-terracotta italic font-medium">{t.heroAccent}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="text-white/80 text-lg md:text-xl mt-6 max-w-xl leading-relaxed"
          >
            {t.heroSub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-3 mt-10"
          >
            <Link to="/alakart">
              <Button data-testid="hero-order-cta"
                className="group h-14 px-8 rounded-full bg-terracotta text-white hover:bg-terracotta/90 font-semibold tracking-wide shadow-2xl shadow-terracotta/30 hover:shadow-terracotta/50 hover:scale-[1.02] active:scale-95 transition-all">
                {t.orderNow}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/catering">
              <Button data-testid="hero-menu-cta"
                className="h-14 px-8 rounded-full bg-white/10 backdrop-blur-md ring-1 ring-white/30 text-white hover:bg-white/20 font-semibold tracking-wide">
                {t.seeMenu}
              </Button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Hours pill — top right */}
        <motion.div
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 }}
          className="absolute top-24 right-6 md:top-28 md:right-12 z-20 flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md ring-1 ring-white/20 text-white text-sm"
          data-testid="hero-hours-pill"
        >
          <span className={`w-2 h-2 rounded-full ${isOpen ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}`} />
          <span className="font-medium">{isOpen ? t.open : t.closed}</span>
          {openMessage && openMessage !== t.open && openMessage !== t.closed && (
            <span className="hidden md:inline text-white/70 text-xs">· {openMessage}</span>
          )}
        </motion.div>
      </section>

      {/* === SOCIAL PROOF RIBBON === */}
      <section className="bg-charcoal text-white/90 py-6 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <div className="flex items-center gap-1">
            {[0,1,2,3,4].map(i => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}
          </div>
          <p className="font-medium tracking-wide text-sm md:text-base">{t.socialProof}</p>
        </div>
      </section>

      {/* === BENTO SHOWCASE === */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-12 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold">— Vitrin</span>
            <h2 className="font-playfair text-4xl md:text-5xl tracking-tight mt-3 mb-3">{t.showcaseTitle}</h2>
            <p className="text-charcoal/60 leading-relaxed">{t.showcaseSub}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 md:grid-rows-2 gap-4 md:gap-5 h-auto md:h-[560px]">
            <ShowcaseCard to="/alakart" img={ALAKART_IMG} icon={UtensilsCrossed}
              label={t.alakart} desc={t.alakartDesc}
              className="md:col-span-7 md:row-span-2 h-72 md:h-auto" testid="showcase-alakart" featured />
            <ShowcaseCard to="/icecekler" img={DRINKS_IMG} icon={Wine}
              label={t.beverages} desc={t.beveragesDesc}
              className="md:col-span-5 md:row-span-1 h-56 md:h-auto" testid="showcase-beverages" />
            <ShowcaseCard to="/catering" img={CATERING_IMG} icon={ChefHat}
              label={t.catering} desc={t.cateringDesc}
              className="md:col-span-5 md:row-span-1 h-56 md:h-auto" testid="showcase-catering" />
          </div>
        </div>
      </section>

      {/* === FEATURES + QR + HOURS === */}
      <SmartRecommendations />

      <section className="bg-sand py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="font-playfair text-4xl md:text-5xl tracking-tight mb-12 text-center md:text-left">
            {t.whyUs}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {t.whyFeatures.map((f, i) => (
              <motion.div key={f.title}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="bg-bone rounded-3xl p-8 ring-1 ring-charcoal/5 hover:ring-terracotta/40 hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center mb-5">
                  <f.icon className="w-7 h-7" />
                </div>
                <h3 className="font-playfair text-2xl mb-2">{f.title}</h3>
                <p className="text-charcoal/60 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* QR card + Hours card row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              className="relative overflow-hidden rounded-3xl bg-charcoal text-white p-8 md:p-10">
              <div className="relative z-10 max-w-md">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-terracotta/20 ring-1 ring-terracotta/40 mb-5">
                  <QrCode className="w-7 h-7 text-terracotta" />
                </div>
                <h3 className="font-playfair text-3xl mb-2">{t.qrTitle}</h3>
                <p className="text-white/70 leading-relaxed">{t.qrDesc}</p>
              </div>
              <img src={AMBIANCE_IMG} alt="" className="absolute right-0 bottom-0 w-1/2 h-full object-cover opacity-25 mix-blend-luminosity" />
            </motion.div>

            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              className="rounded-3xl bg-bone ring-1 ring-charcoal/5 p-8 md:p-10 flex flex-col justify-between" data-testid="hours-card">
              <div>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-basil/15 text-basil mb-5">
                  <Clock className="w-7 h-7" />
                </div>
                <h3 className="font-playfair text-3xl mb-2">{t.hoursTitle}</h3>
                <p className="text-charcoal/60 leading-relaxed">
                  {(openMessage && openMessage !== t.open && openMessage !== t.closed)
                    ? openMessage
                    : (isOpen ? 'Şu anda hizmetinizdeyiz — sıcacık siparişlerinizi bekliyoruz.' : 'Şu an kapalıyız. Çalışma saatlerinde tekrar bekleriz.')}
                </p>
              </div>
              <div className="mt-6 flex items-center gap-2">
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${isOpen ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  <span className={`w-2 h-2 rounded-full ${isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                  {isOpen ? t.open : t.closed}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* === MAP + CONTACT === */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 rounded-3xl overflow-hidden ring-1 ring-charcoal/10 bg-charcoal/5 min-h-[400px]">
            <iframe
              title="Central Hotel Map"
              src={RESTAURANT.mapsEmbed}
              width="100%" height="100%"
              style={{ border: 0, minHeight: 400 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="lg:col-span-2 bg-charcoal text-white rounded-3xl p-8 md:p-10 flex flex-col justify-between">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold">— {t.contactTitle}</span>
              <h2 className="font-playfair text-4xl md:text-5xl tracking-tight mt-3 mb-8">{t.tagline}</h2>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-terracotta/20 ring-1 ring-terracotta/30 text-terracotta flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">{t.address}</p>
                    <p className="font-medium">{RESTAURANT.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-basil/20 ring-1 ring-basil/30 text-basil flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">{t.phone}</p>
                    <a href={`tel:${RESTAURANT.phone.replace(/\s/g, '')}`} className="font-medium hover:text-terracotta transition-colors">
                      {RESTAURANT.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a href={`tel:${RESTAURANT.phone.replace(/\s/g, '')}`} className="flex-1">
                <Button className="w-full h-12 rounded-full bg-terracotta text-white hover:bg-terracotta/90 font-semibold" data-testid="contact-call-btn">
                  <Phone className="w-4 h-4 mr-2" />{t.callNow}
                </Button>
              </a>
              <a href={RESTAURANT.mapsUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button className="w-full h-12 rounded-full bg-white/10 ring-1 ring-white/20 text-white hover:bg-white/20 font-semibold" data-testid="contact-directions-btn">
                  <MapPin className="w-4 h-4 mr-2" />{t.getDirections}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// === Bento showcase card ===
const ShowcaseCard = ({ to, img, icon: Icon, label, desc, className = '', testid, featured }) => (
  <Link to={to} className={`group relative rounded-3xl overflow-hidden ring-1 ring-charcoal/5 ${className}`} data-testid={testid}>
    <img src={img} alt={label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/30 to-transparent" />
    <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5 text-terracotta" />
        <span className="text-xs uppercase tracking-[0.25em] text-white/70 font-semibold">{featured ? 'Öne Çıkan' : ''}</span>
      </div>
      <h3 className={`font-playfair text-white tracking-tight ${featured ? 'text-4xl md:text-5xl' : 'text-2xl md:text-3xl'}`}>{label}</h3>
      <p className="text-white/70 mt-2 max-w-md">{desc}</p>
      <span className="inline-flex items-center gap-1 mt-4 text-terracotta font-semibold text-sm group-hover:gap-2 transition-all">
        Keşfet <ArrowRight className="w-4 h-4" />
      </span>
    </div>
  </Link>
);

export default Home;
